import { useCallback, useState } from 'react';
import type { BadgeId, CelebrationCue, LevelIdentity } from '@contracts';

/**
 * Sequences a `CelebrationCue` (the REAL cue returned by domain commands —
 * `completeAction`, `acceptObjectiveDateSuggestion`, per phase-2a-rewards.md
 * RW-1/RW-2; never fake/local state, per this round's brief) into the XP
 * toast + level-up moment + badge-earn sheet queue described in
 * badges-and-celebrations.md: "queued after any level-up, one moment at a
 * time, never stacked audio-visual chaos" (SS1.4), and level-up plays before
 * any badge sheet if both fire from the same cue (SS5: "if a badge earn
 * coincides, level-up plays first, badge sheet follows").
 *
 * `resolveIdentity`/`resolveBadge` are injected lookups (level number ->
 * LevelIdentity, BadgeId -> BadgeView) rather than baked in, so this hook
 * stays pure sequencing logic with no direct DomainApi dependency — callers
 * (TodayView, ObjectiveDetailScreen) already hold the loaded RewardConfig /
 * badge list from their own query and pass the lookups through.
 */
export interface CelebrationQueueState {
  readonly xpToast: { readonly amount: number; readonly sourceLabel: string } | null;
  readonly levelUp: { readonly level: number; readonly identity: LevelIdentity; readonly xpTotal: number } | null;
  readonly badgeQueue: readonly BadgeId[];
}

export function useCelebrationQueue(options: {
  readonly resolveIdentity: (level: number) => LevelIdentity;
}) {
  const [xpToast, setXpToast] = useState<CelebrationQueueState['xpToast']>(null);
  const [levelUp, setLevelUp] = useState<CelebrationQueueState['levelUp']>(null);
  const [badgeQueue, setBadgeQueue] = useState<readonly BadgeId[]>([]);

  const enqueue = useCallback(
    (cue: CelebrationCue, sourceLabel: string, xpTotalAfter: number) => {
      if (cue.xpAwarded > 0) {
        setXpToast((existing) =>
          // Coalescing (SS2 "rewards landing within the hold window merge"):
          // this simple version merges any toast still showing rather than
          // tracking a real hold-window timer, which is a reasonable
          // approximation given XP-earning commands in this build are
          // triggered by discrete user taps, not rapid-fire batches.
          existing ? { amount: existing.amount + cue.xpAwarded, sourceLabel } : { amount: cue.xpAwarded, sourceLabel },
        );
      }
      if (cue.levelUp) {
        setLevelUp({
          level: cue.levelUp.newLevel,
          identity: options.resolveIdentity(cue.levelUp.newLevel),
          xpTotal: xpTotalAfter,
        });
      }
      if (cue.badgesEarned.length > 0) {
        setBadgeQueue((prev) => [...prev, ...cue.badgesEarned]);
      }
    },
    [options],
  );

  const dismissToast = useCallback(() => setXpToast(null), []);

  const dismissLevelUp = useCallback(() => setLevelUp(null), []);

  const dismissNextBadge = useCallback(() => {
    setBadgeQueue((prev) => prev.slice(1));
  }, []);

  // Badge sheets wait for the level-up moment to clear first (spec: level-up
  // plays first, badge sheet follows) — the currently-visible badge is only
  // the head of the queue, and only once levelUp is null.
  const visibleBadgeId = levelUp === null ? (badgeQueue[0] ?? null) : null;

  return {
    xpToast,
    levelUp,
    visibleBadgeId,
    enqueue,
    dismissToast,
    dismissLevelUp,
    dismissNextBadge,
  };
}
